import { useLazyQuery } from '@apollo/client'
import React, { useEffect, useState, MouseEventHandler } from 'react'
import { GET_PRODUCT_BY_FILTER } from '../../../../../utils/GraphQLAPI'
import '../../../../shared-css/tagify.css'

import Select, { components, MultiValueProps, OnChangeValue, OptionProps, Props } from 'react-select'

import {
  SortableContainer,
  SortableContainerProps,
  SortableElement,
  SortEndHandler,
} from 'react-sortable-hoc'
import { arrayMove } from '../../../../../utils/helper'

interface PropsValue {
  formik: any
  salesDatas: any
  label: any
  isEdit: boolean
  salesProducts: any
}

export interface ColourOption {
  readonly value: string
  readonly label: string
  readonly color: string
  readonly isFixed?: boolean
  readonly isDisabled?: boolean
}

//  Custom MultiValue (selected tag) with tooltip
const SortableMultiValueWithTooltip = SortableElement<MultiValueProps<ColourOption, true>>(
  (props: any) => {
    const onMouseDown: MouseEventHandler<HTMLDivElement> = (e) => {
      e.preventDefault()
      e.stopPropagation()
    }
    const innerProps = {
      ...props.innerProps,
      onMouseDown,
      title: `Product-Code: ${props.data.prodId ?? ''}`,
    }

    return <components.MultiValue {...props} innerProps={innerProps} />
  }
)

const SortableSelect = SortableContainer(Select) as React.ComponentClass<
  Props<ColourOption, true> & SortableContainerProps
>

const productSetup = (product: any) => {
  return {
    id: product.prod_id,
    value: product.prod_id,
    label: product.prod_name,
    name: product.prod_name,
    prodId: product.prod_code,
    __typename: 'ProductsByFilter',
  }
}

const ProductDetails = ({ formik, salesDatas, label, isEdit, salesProducts }: PropsValue) => {
  const [chooseProducts, setChooseProducts] = useState<any>([])
  const [serverProductId, setServerProductId] = useState<any>([])
  const [selected, setSelected] = useState<any>([])
  const [removedId, setRemovedId] = useState<any>([])
  const [selectedId, setSelectedId] = useState<any>([])
  const selectOption = {
    id: 'select',
    value: '',
    label: 'Select All',
    name: 'Select All',
    prodId: '',
    __typename: 'SalesChannel',
  }
  const mappedProducts = chooseProducts.map((channelName: any) => {
    return {
      id: channelName.id,
      value: channelName.id,
      label: channelName.name,
      name: channelName.name,
      prodId: channelName.gcode ? channelName.gcode : channelName.ecode,
      __typename: 'SalesChannel',
    }
  })

  useEffect(() => {
    if (salesDatas?.products?.length >= 0) {
      const products = salesDatas?.products
      const productIds = products.map((product: any) => productSetup(product))
      setServerProductId(productIds)
      setSelected(productIds)
      setSelectedId([])
    }

    if (!salesProducts) {
      getProductDetails()
    } else {
      setChooseProducts(salesProducts.map((product: any) => productSetup(product)))
    }
  }, [salesDatas, isEdit])

  const onChange = (selectedOptions: OnChangeValue<any, true>, actionType: any) => {
    setSelected(actionType?.option?.id === 'select' ? mappedProducts : selectedOptions)

    if (actionType.action === 'select-option') {
      if (
        serverProductId.length === 0 ||
        serverProductId.every((item: any) => item.id !== actionType.option.id)
      ) {
        if (actionType?.option?.id === 'select') {
          const newValues = mappedProducts.filter(
            (channelName: any) => !selectedId.includes(channelName.id)
          )
          const newIds = newValues.map((channelName: any) => channelName.id)
          const updatedSelectedId = [...selectedId, ...newIds]

          setSelectedId(updatedSelectedId)
          formik.setFieldValue('productIds', updatedSelectedId)
        } else {
          const updatedSelectedId = [...selectedId, actionType.option.id]
          setSelectedId(updatedSelectedId)
          formik.setFieldValue('productIds', updatedSelectedId)
        }
      }
    }
    // Add support for 'pop-value' triggered by backspace when removing a selected item
    else if (actionType?.action === 'remove-value' || actionType?.action === 'pop-value') {
      if (serverProductId.some((item: any) => item.id === actionType.removedValue.id)) {
        const updatedRemovedId = [...removedId, actionType.removedValue.id]
        setRemovedId(updatedRemovedId)
        formik.setFieldValue('deletedProductId', updatedRemovedId)
      }

      if (selectedId.includes(actionType.removedValue.id)) {
        const filteredArray = selectedId.filter((e: any) => e !== actionType.removedValue.id)
        setSelectedId(filteredArray)
        formik.setFieldValue('productIds', filteredArray)
      }
    } else if (actionType.action === 'clear') {
      const removedIds = actionType.removedValues.map((item: any) => item.id || item.value)
      setRemovedId(removedIds)
      formik.setFieldValue('deletedProductId', removedIds)
    }
  }

  const onSortEnd: SortEndHandler = ({ oldIndex, newIndex }) => {
    const newValue = arrayMove(selected, oldIndex, newIndex)
    setSelected(newValue)
  }

  const [getProductDetails] = useLazyQuery(GET_PRODUCT_BY_FILTER, {
    fetchPolicy: 'network-only',
    onCompleted: (data: any) => {
      if (formik.touched.utility || formik.touched.commodity || formik.touched.customerType) {
        setChooseProducts(
          data?.getProductByFilter?.map((x: any) => ({
            ...x,
          }))
        )
      } else {
        setChooseProducts(data?.getProductByFilter)
      }
    },
    variables: {
      salesChannelProductInput: {
        commodityId: formik.getFieldMeta('commodityId').value,
        customerTypeId: formik.getFieldMeta('customerTypeId').value,
        utilityId: formik.getFieldMeta('utilityId').value,
      },
    },
  })

  return (
    <>
      <label className='d-flex align-items-center mb-2'>
        <span className='fs-6 fw-semibold lh-23px'>{label}</span>
      </label>

      <SortableSelect
        maxMenuHeight={170}
        useDragHandle
        axis='xy'
        isDisabled={isEdit}
        onSortEnd={onSortEnd}
        distance={4}
        getHelperDimensions={({ node }) => node.getBoundingClientRect()}
        isMulti
        options={[selectOption, ...mappedProducts]}
        value={selected}
        onChange={onChange}
        components={{
          MultiValue: SortableMultiValueWithTooltip, //  Show tooltip on selected items
        }}
        closeMenuOnSelect={false}
      />
    </>
  )
}

export default ProductDetails
